import { isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useUpdateOrderMutation } from '../../apis/mlkApi/mlk.api.hooks';
import { parseRes } from '../../apis/utils';
import { AddressInstructions } from '../../components/AddressInstructions/AddressInstructions';
import { Button } from '../../components/Button/Button';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import { StrongRegistrationWrap } from '../../components/StrongRegistrationWrap/StrongRegistrationWrap';
import { useIsEditNotesEnabled } from '../../hooks/mlkActions/useIsEditNotesEnabled';
import { useBack } from '../../hooks/useBack';
import {
  EditAddressNotesForm,
  useEditAddressNotesValidationForm,
} from '../../hooks/validations/useEditAddressNotesValidationForm';
import { useLanguage } from '../../locale/useLanguage';
import { OperationType } from '../../models';
import { setOperation } from '../../redux/general/general.actions';
import { Routes } from '../../routes/types';

import './EditAddressNotesView.css';

type EditAddressNotesProps = {};

export const EditAddressNotes: React.FC<EditAddressNotesProps> = () => {
  const [t] = useLanguage();
  const back = useBack();
  const history = useHistory();
  const dispatch = useDispatch();

  const isEditAddressNotesEnabled = useIsEditNotesEnabled();
  const [{ data: order }, setOrder] = useState<OrderRequestResult>({
    data: undefined,
    isFetching: false,
    isLoading: false,
  });
  const [updateOrder] = useUpdateOrderMutation();

  const startingNotes: EditAddressNotesForm | undefined = useMemo(
    () =>
      order && {
        floor: order.deliveryAddress.floorNumber,
        intercomName: order.deliveryAddress.doorbell,
        notes: order.deliveryAddress.instructions,
        hasElevator: order.deliveryAddress.elevator,
      },
    [order]
  );

  const {
    addressTypeField,
    errors,
    floorValidation,
    hasElevatorValidation,
    intercomNameValidation,
    notesValidation,
    handleSubmit,
    onAddressTypeChange,
  } = useEditAddressNotesValidationForm(startingNotes);

  useEffect(() => {
    if (!isNil(isEditAddressNotesEnabled) && !isEditAddressNotesEnabled) {
      history.replace(Routes.Home);
    }
  }, [history, isEditAddressNotesEnabled]);

  const removeOperationAndBack = useCallback(() => {
    dispatch(setOperation(null));
    back();
  }, [back, dispatch]);

  const updateNotes = useMemo(() => {
    return handleSubmit((notes) => {
      updateOrder({
        orderId: `${order!.id}`,
        instructions: {
          floorNumber: notes.floor ? +notes.floor : undefined,
          doorbell: notes.intercomName,
          elevator: notes.hasElevator,
          instructions: notes.notes,
        },
      }).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        if (successRes.data) {
          history.push(Routes.Home);
        }
        if (errorRes.error) {
          // TODO: Handle error
        }
      });
    });
  }, [handleSubmit, history, order, updateOrder]);

  return (
    <Layout
      hasBack
      onOrderChange={setOrder}
      onBackClick={removeOperationAndBack}
      title={t('pageTitles.editAddressNotes')}
    >
      <div className="edit-address-notes--container">
        <AddressInstructions
          addressType={addressTypeField.value}
          floorValidation={floorValidation}
          hasElevatorValidation={hasElevatorValidation}
          intercomNameValidation={intercomNameValidation}
          errors={errors}
          notesValidation={notesValidation}
          onAddressTypeChange={onAddressTypeChange}
        />
        <div className="edit-address-notes--button">
          <StrongRegistrationWrap
            operationType={OperationType.AddNotes}
            successAction={updateNotes}
          >
            <Button label={t('changeAddress.editAddressNotes')} isPrimary />
          </StrongRegistrationWrap>
        </div>
      </div>
    </Layout>
  );
};
